// src/components/EditScreen.js
import React, { useState } from 'react';
import CombineMediaTab from './CombineMediaTab';
import ConcatenateVideosTab from './ConcatenateVideosTab';

function EditScreen() {
  const [activeTab, setActiveTab] = useState('combine');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 画像/動画と音声を合成する関数
  const combineMedias = async (mediaFile, audioFile) => {
    try {
      setLoading(true);
      setMessage('メディアを処理中...');

      // ファイル拡張子と種類を確認
      const mediaFileName = mediaFile.name;
      const mediaType = mediaFile.type;
      const isImage = mediaType.startsWith('image/');

      if (isImage) {
        // 画像 + 音声の場合
        return await combineImageAndAudio(mediaFile, audioFile);
      } else {
        // 動画 + 音声の場合
        return await combineVideoAndAudio(mediaFile, audioFile);
      }
    } catch (error) {
      console.error('メディア処理エラー:', error);
      setLoading(false);
      setMessage(`エラーが発生しました: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 画像と音声から動画を作成
  const combineImageAndAudio = async (imageFile, audioFile) => {
    return new Promise(async (resolve, reject) => {
      try {
        setMessage('画像と音声を合成中...');

        // 画像の読み込み
        const imageUrl = URL.createObjectURL(imageFile);
        const image = new Image();
        await new Promise((resolve) => {
          image.onload = resolve;
          image.src = imageUrl;
        });

        // 音声の読み込み
        const audioUrl = URL.createObjectURL(audioFile);
        const audio = new Audio();
        await new Promise((resolve) => {
          audio.onloadedmetadata = resolve;
          audio.src = audioUrl;
        });

        // キャンバスの作成
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        // MediaRecorderの設定
        const stream = canvas.captureStream(30); // 30fps
        
        // 音声トラックの追加
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(audio);
        const destination = audioCtx.createMediaStreamDestination();
        source.connect(destination);
        source.connect(audioCtx.destination);
        
        const audioTrack = destination.stream.getAudioTracks()[0];
        stream.addTrack(audioTrack);

        // 録画開始
        const recorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp9,opus'
        });
        
        const chunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          URL.revokeObjectURL(imageUrl);
          URL.revokeObjectURL(audioUrl);
          setMessage('処理が完了しました！');
          resolve(blob);
        };
        
        recorder.start();
        audio.play();
        
        // 音声の長さに合わせて録画停止
        setTimeout(() => {
          recorder.stop();
          audio.pause();
        }, audio.duration * 1000);
      } catch (error) {
        reject(error);
      }
    });
  };

  // 動画と音声を合成
  const combineVideoAndAudio = async (videoFile, audioFile) => {
    return new Promise(async (resolve, reject) => {
      try {
        setMessage('動画と音声を合成中...');

        // 動画の読み込み
        const videoUrl = URL.createObjectURL(videoFile);
        const video = document.createElement('video');
        await new Promise((resolve) => {
          video.onloadedmetadata = resolve;
          video.src = videoUrl;
        });

        // 音声の読み込み
        const audioUrl = URL.createObjectURL(audioFile);
        const audio = new Audio();
        await new Promise((resolve) => {
          audio.onloadedmetadata = resolve;
          audio.src = audioUrl;
        });

        // キャンバスの作成
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        // MediaRecorderの設定
        const stream = canvas.captureStream(30); // 30fps

        // 音声トラックの追加
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(audio);
        const destination = audioCtx.createMediaStreamDestination();
        source.connect(destination);
        source.connect(audioCtx.destination);
        
        const audioTrack = destination.stream.getAudioTracks()[0];
        stream.addTrack(audioTrack);

        // 録画開始
        const recorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp9,opus'
        });
        
        const chunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          URL.revokeObjectURL(videoUrl);
          URL.revokeObjectURL(audioUrl);
          setMessage('処理が完了しました！');
          resolve(blob);
        };
        
        recorder.start();
        
        // 動画の再生と描画
        video.play();
        audio.play();
        
        const duration = Math.min(video.duration, audio.duration);
        
        const drawFrame = () => {
          if (video.currentTime < duration) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            requestAnimationFrame(drawFrame);
          } else {
            recorder.stop();
            video.pause();
            audio.pause();
          }
        };
        
        drawFrame();
      } catch (error) {
        reject(error);
      }
    });
  };

  // 複数の動画を連結する関数
  const concatenateVideos = async (videoFiles) => {
    try {
      setLoading(true);
      setMessage('動画を連結中...');

      // 各動画を読み込む
      const videos = await Promise.all(
        videoFiles.map(file => {
          return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;
            video.src = URL.createObjectURL(file);
            video.onloadedmetadata = () => resolve(video);
          });
        })
      );

      // キャンバスのサイズを最初の動画に合わせる
      const canvas = document.createElement('canvas');
      canvas.width = videos[0].videoWidth;
      canvas.height = videos[0].videoHeight;
      const ctx = canvas.getContext('2d');

      // MediaRecorderの設定
      const stream = canvas.captureStream(30); // 30fps
      
      // 最初の動画から音声を取得（可能であれば）
      if (videos[0].audioTracks && videos[0].audioTracks.length > 0) {
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(videos[0]);
        const destination = audioCtx.createMediaStreamDestination();
        source.connect(destination);
        source.connect(audioCtx.destination);
        
        const audioTrack = destination.stream.getAudioTracks()[0];
        if (audioTrack) {
          stream.addTrack(audioTrack);
        }
      }
      
      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9,opus'
      });
      
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        
        // URLを解放
        videos.forEach(video => {
          URL.revokeObjectURL(video.src);
        });
        
        setMessage('処理が完了しました！');
        setLoading(false);
        
        return blob;
      };
      
      // 録画開始
      recorder.start();
      
      // 各動画を順番に再生
      let currentIndex = 0;
      
      const playNextVideo = () => {
        if (currentIndex >= videos.length) {
          recorder.stop();
          return;
        }
        
        const video = videos[currentIndex];
        video.currentTime = 0;
        video.play();
        
        const drawFrame = () => {
          if (video.ended || video.paused) {
            currentIndex++;
            playNextVideo();
            return;
          }
          
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          requestAnimationFrame(drawFrame);
        };
        
        drawFrame();
      };
      
      // 最初の動画から再生開始
      playNextVideo();
      
      // 全ての処理が終わるのを待つ
      const totalDuration = videos.reduce((sum, video) => sum + video.duration, 0) * 1000;
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          const resultBlob = new Blob(chunks, { type: 'video/webm' });
          resolve(resultBlob);
        }, totalDuration + 1000); // 少し余裕を持たせる
      });
      
      return result;
      
    } catch (error) {
      console.error('動画連結エラー:', error);
      setLoading(false);
      setMessage(`エラーが発生しました: ${error.message}`);
      return null;
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">メディア編集ツール</h2>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex border-b">
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'combine' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('combine')}
          >
            画像/動画 + 音声の合成
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'concatenate' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('concatenate')}
          >
            複数動画の連結
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'combine' && (
            <CombineMediaTab 
              onProcess={combineMedias} 
              isLoading={loading} 
              message={message}
            />
          )}
          {activeTab === 'concatenate' && (
            <ConcatenateVideosTab 
              onProcess={concatenateVideos} 
              isLoading={loading} 
              message={message}
            />
          )}
        </div>
      </div>
      
      <p className="text-xs text-gray-500 mt-4 text-center">
        すべての処理はブラウザ内で実行され、ファイルがサーバーに送信されることはありません。
      </p>
    </div>
  );
}

export default EditScreen;